import { Badge } from '@/components/ui/badge'

/**
 * GoCardless Status Badge Component
 *
 * Displays status badges for GoCardless payments and mandates with appropriate
 * color coding and labels.
 *
 * @param {Object} props
 * @param {'payment'|'mandate'} props.type - Type of status (payment or mandate)
 * @param {string} props.status - Current status value from database
 * @returns {JSX.Element|null} Badge component or null if no status
 */
export function GoCardlessStatusBadge({ type, status }) {
  if (!status) return null

  // Configuration mapping for payment and mandate statuses
  const STATUS_CONFIG = {
    payment: {
      // Pending states (yellow/amber)
      pending_customer_approval: { variant: 'gc-pending', label: 'Pending Approval' },
      pending_submission: { variant: 'gc-pending', label: 'Pending Submission' },
      submitted: { variant: 'gc-pending', label: 'Submitted' },

      // Success states (green)
      confirmed: { variant: 'gc-success', label: 'Confirmed' },
      paid_out: { variant: 'gc-success', label: 'Paid Out' },

      // Failed states (red)
      failed: { variant: 'gc-failed', label: 'Failed' },
      customer_approval_denied: { variant: 'gc-failed', label: 'Denied' },
      charged_back: { variant: 'gc-failed', label: 'Charged Back' },
      late_failure_settled: { variant: 'gc-failed', label: 'Late Failure' },

      // Cancelled states (gray)
      cancelled: { variant: 'gc-cancelled', label: 'Cancelled' },
    },
    mandate: {
      // Pending states (yellow/amber)
      pending_customer_approval: { variant: 'gc-pending', label: 'Pending Approval' },
      pending_submission: { variant: 'gc-pending', label: 'Pending Submission' },
      submitted: { variant: 'gc-pending', label: 'Submitted' },

      // Success state (green)
      active: { variant: 'gc-success', label: 'Active' },

      // Failed states (red)
      failed: { variant: 'gc-failed', label: 'Failed' },
      expired: { variant: 'gc-failed', label: 'Expired' },

      // Cancelled/Inactive states (gray)
      cancelled: { variant: 'gc-cancelled', label: 'Cancelled' },
      consumed: { variant: 'gc-cancelled', label: 'Consumed' },
      blocked: { variant: 'gc-cancelled', label: 'Blocked' },
      suspended: { variant: 'gc-cancelled', label: 'Suspended' },
    }
  }

  const config = STATUS_CONFIG[type]?.[status]

  if (!config) {
    console.warn(`[GoCardlessStatusBadge] Unknown ${type} status: ${status}`)
    return null
  }

  return (
    <Badge variant={config.variant}>
      {config.label}
    </Badge>
  )
}

export default GoCardlessStatusBadge
